/**
 * BankConnectionsCard
 * The treasurer-facing connection-health surface for Plaid bank-pull (#563), on
 * the Banks & processors setup page beneath the "Link a bank" entry point. Lists
 * each linked connection's status and lets the treasurer:
 *   - Reconnect a `LoginRequired` connection via Plaid Link update mode — a two-step
 *     handshake (mint an update-mode token → confirm on Link success) that returns it
 *     to Active WITHOUT re-selecting accounts (the cursor + tracked accounts are kept).
 *   - Disconnect a live connection (confirm-gated, with an irreversibility note —
 *     re-linking later creates a fresh Plaid Item).
 *   - Re-link a permanently-Disconnected connection: a FRESH Link (new Plaid Item →
 *     exchange → account selection), gated by a click-time duplicate warning because a
 *     fresh Item re-imports overlapping transactions as duplicates (ADR-075).
 *
 * CSV / OFX upload stays available as the always-there fallback (Sources page); a
 * persistent note keeps that path visible regardless of connection state.
 *
 * Self-gates on Finance:Manage and renders nothing below that tier, mirroring
 * `LinkBankFlow`. Renders nothing until at least one bank is linked — the empty
 * state lives in the Link-a-bank entry point above.
 */

import { useMemo, useState } from 'react';
import { Landmark, Upload } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import type { BankConnectionView, BankConnectionLinkResult } from '@signalvote/shared';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useCampaignStore } from '@/stores';
import {
  useBankConnections,
  useReconnectBankToken,
  useReconnectBankConnection,
  useDisconnectBankConnection,
  useSyncBankConnection,
  useCreateBankLinkToken,
  useExchangePublicToken,
} from '@/hooks/queries/useBankConnections';
import { answered } from '@/lib/reportsRead';
import { bankUnreadyMessage } from '@/lib/bankReads';
import { BankConnectionItem } from './BankConnectionItem';
import { BankAccountSelection } from './BankAccountSelection';
import { PlaidLinkOpener } from './PlaidLinkOpener';
import {
  savePlaidLinkSession,
  clearPlaidLinkSession,
  resumePlaidLink,
} from './plaidLinkSession';

export function BankConnectionsCard() {
  const canManage = useCampaignStore((s) => s.canManage('Finance'));

  const connectionsRead = useBankConnections();
  const connections = answered(connectionsRead);
  const connectionsUnready = bankUnreadyMessage('your linked banks', connectionsRead);
  const { mutateAsync: mintReconnectToken } = useReconnectBankToken();
  const { mutateAsync: confirmReconnect } = useReconnectBankConnection();
  const { mutateAsync: disconnect } = useDisconnectBankConnection();
  const { mutateAsync: syncConnection } = useSyncBankConnection();
  const { mutateAsync: createLinkToken } = useCreateBankLinkToken();
  const { mutateAsync: exchangePublicToken } = useExchangePublicToken();

  // On an OAuth return (#621), an OAuth bank redirected the treasurer back here
  // (carrying `oauth_state_id`) after a full navigation that wiped all state.
  // Restore whichever handshake was mid-flight — reconnect (update mode) or relink
  // (fresh Item) — from the session stashed before the hand-off, and resume Link with
  // `receivedRedirectUri` set. Each memo fires ONLY for its own flow, so a reconnect
  // never picks up a relink's redirect. Computed once; null on a normal mount.
  const reconnectResume = useMemo(() => resumePlaidLink('reconnect'), []);
  const relinkResume = useMemo(() => resumePlaidLink('relink'), []);

  // `reconnectingId` present ⇒ a reconnect handshake is in flight for that connection;
  // `reconnectToken` present ⇒ Plaid Link (update mode) is open against it.
  const [reconnectingId, setReconnectingId] = useState<string | null>(
    reconnectResume?.connectionId ?? null,
  );
  const [reconnectToken, setReconnectToken] = useState<string | null>(
    reconnectResume?.token ?? null,
  );
  // Set ONLY for the OAuth-resumed reconnect — a fresh open clears it.
  const [reconnectRedirectUri, setReconnectRedirectUri] = useState<string | null>(
    reconnectResume?.receivedRedirectUri ?? null,
  );
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);
  // `syncingId` present ⇒ a manual "sync now" is in flight for that connection.
  const [syncingId, setSyncingId] = useState<string | null>(null);

  // Re-link (Disconnected recovery, ADR-075) is a FRESH Link — a new Plaid Item, not the
  // update-mode reconnect. `relinkingId` ⇒ minting/exchanging for that row; `relinkToken`
  // ⇒ Plaid Link is open for the fresh link; `relinkExchange` ⇒ on the account-selection step.
  const [relinkingId, setRelinkingId] = useState<string | null>(
    relinkResume?.connectionId ?? null,
  );
  const [relinkToken, setRelinkToken] = useState<string | null>(relinkResume?.token ?? null);
  // Set ONLY for the OAuth-resumed relink — a fresh open clears it.
  const [relinkRedirectUri, setRelinkRedirectUri] = useState<string | null>(
    relinkResume?.receivedRedirectUri ?? null,
  );
  const [relinkExchange, setRelinkExchange] = useState<BankConnectionLinkResult | null>(null);

  if (!canManage) return null;

  // Step 1 of reconnect: mint the update-mode Link token for the SAME Item, then open Link.
  const handleReconnect = async (connection: BankConnectionView) => {
    setReconnectingId(connection.id);
    setReconnectToken(null);
    setReconnectRedirectUri(null); // a fresh open is not an OAuth resume
    try {
      const { linkToken } = await mintReconnectToken(connection.id);
      // Persist BEFORE opening Link so an OAuth hand-off survives the round-trip and
      // resumes into THIS connection's reconnect.
      savePlaidLinkSession({ token: linkToken, flow: 'reconnect', connectionId: connection.id });
      setReconnectToken(linkToken);
    } catch {
      // Minting failed (e.g. already disconnected) — drop back out of the handshake.
      setReconnectingId(null);
    }
  };

  // Step 2 of reconnect: Plaid Link update mode finished — re-activate the SAME connection.
  // Update mode re-auths the existing Item, so there is no public-token exchange and no
  // account re-selection; we just confirm the reconnect.
  const handleReconnectSuccess = async () => {
    const id = reconnectingId;
    setReconnectToken(null);
    setReconnectRedirectUri(null);
    clearPlaidLinkSession();
    if (!id) return;
    try {
      await confirmReconnect(id);
    } finally {
      setReconnectingId(null);
    }
  };

  // Treasurer closed Link without finishing — abandon the handshake and clear the session.
  const handleReconnectExit = () => {
    setReconnectToken(null);
    setReconnectRedirectUri(null);
    setReconnectingId(null);
    clearPlaidLinkSession();
  };

  // Sync now — the free `/transactions/sync` cached pull for a live connection. The
  // per-connection gate on the server 409s a stacked sync; here we just disable the row's
  // button while its own request is in flight. Success invalidates the finance tree (in the
  // hook) so the last-synced time + any new rows surface.
  const handleSync = async (connection: BankConnectionView) => {
    setSyncingId(connection.id);
    try {
      await syncConnection(connection.id);
    } catch {
      // Swallow — a failed/again-in-progress sync leaves the connection untouched; the
      // treasurer can retry. (The daily poll keeps the ledger current regardless.)
    } finally {
      setSyncingId(null);
    }
  };

  const handleDisconnect = async (connection: BankConnectionView) => {
    const confirmed = window.confirm(
      `Disconnect ${connection.institutionName ?? 'this bank'}? This removes the Plaid ` +
        'connection and stops pulling new transactions. Re-linking later creates a fresh ' +
        'connection — this cannot be undone.',
    );
    if (!confirmed) return;
    setDisconnectingId(connection.id);
    try {
      await disconnect(connection.id);
    } finally {
      setDisconnectingId(null);
    }
  };

  // Re-link a permanently-Disconnected bank. ADR-075: a fresh Item re-imports overlapping
  // transactions as duplicates (dedup keys on the old Item can't carry over) — so we WARN
  // at click-time, before starting Link, and the treasurer clears them manually on the
  // ledger. We accept-and-warn here rather than auto-dedup.
  const handleRelink = async (connection: BankConnectionView) => {
    const name = connection.institutionName ?? 'this bank';
    const confirmed = window.confirm(
      `Re-linking ${name} creates a brand-new connection. Any transactions that overlap ` +
        'dates you already pulled will re-import as duplicates you must delete manually on ' +
        'the ledger — reconnecting cannot merge them back. Continue?',
    );
    if (!confirmed) return;
    setRelinkingId(connection.id);
    setRelinkToken(null);
    setRelinkRedirectUri(null); // a fresh open is not an OAuth resume
    setRelinkExchange(null);
    try {
      const { linkToken } = await createLinkToken();
      // Persist BEFORE opening Link so an OAuth hand-off survives the round-trip and
      // resumes into THIS connection's re-link.
      savePlaidLinkSession({ token: linkToken, flow: 'relink', connectionId: connection.id });
      setRelinkToken(linkToken);
    } catch {
      // Minting failed — drop back out of the re-link.
      setRelinkingId(null);
    }
  };

  // Fresh Link finished — exchange the public token for the new connection, then move to
  // account selection (same as the first-time Link flow).
  const handleRelinkSuccess = async (publicToken: string) => {
    setRelinkToken(null);
    setRelinkRedirectUri(null);
    clearPlaidLinkSession();
    try {
      const result = await exchangePublicToken(publicToken);
      setRelinkExchange(result);
    } finally {
      setRelinkingId(null);
    }
  };

  // Treasurer closed Link without finishing — abandon the fresh link and clear the session.
  const handleRelinkExit = () => {
    setRelinkToken(null);
    setRelinkRedirectUri(null);
    setRelinkingId(null);
    clearPlaidLinkSession();
  };

  // The Link openers render OUTSIDE the loading/empty gate below: on an OAuth return
  // they must resume Link before the connections list has loaded (we already hold the
  // token from storage). When nothing is in flight both tokens are null, so this is an
  // empty fragment and the gate still renders nothing.
  const openers = (
    <>
      {reconnectToken && (
        <PlaidLinkOpener
          token={reconnectToken}
          onSuccess={handleReconnectSuccess}
          onExit={handleReconnectExit}
          receivedRedirectUri={reconnectRedirectUri ?? undefined}
        />
      )}

      {relinkToken && (
        <PlaidLinkOpener
          token={relinkToken}
          onSuccess={handleRelinkSuccess}
          onExit={handleRelinkExit}
          receivedRedirectUri={relinkRedirectUri ?? undefined}
        />
      )}
    </>
  );

  if (connectionsUnready) {
    return (
      <>
        <p role="alert" className="text-sm text-red-700">
          {connectionsUnready}
        </p>
        {openers}
      </>
    );
  }
  if (!connections || connections.length === 0) return openers;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex items-center gap-2">
            <Landmark className="h-5 w-5 text-muted-foreground" aria-hidden />
            <CardTitle className="text-lg">Linked banks</CardTitle>
          </div>
        </CardHeader>

        <CardContent>
          <div className="space-y-3">
            {/* Fresh-link account selection appears here once a re-link exchange lands
                (ADR-075) — the treasurer picks accounts on the brand-new connection. */}
            {relinkExchange && (
              <BankAccountSelection
                exchange={relinkExchange}
                onReset={() => setRelinkExchange(null)}
              />
            )}

            {connections.map((connection) => (
              <BankConnectionItem
                key={connection.id}
                connection={connection}
                onReconnect={() => handleReconnect(connection)}
                onDisconnect={() => handleDisconnect(connection)}
                onRelink={() => handleRelink(connection)}
                onSync={() => handleSync(connection)}
                isReconnecting={reconnectingId === connection.id}
                isDisconnecting={disconnectingId === connection.id}
                isRelinking={relinkingId === connection.id}
                isSyncing={syncingId === connection.id}
              />
            ))}

            {/*
              The manual fallback lives right here in the connection-health card
              — the surface a treasurer lands on when a connection goes bad — as a
              prominent callout with a real link to Sources, not buried grey text.
              Automatic bank-pull never replaces this path.
            */}
            <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2">
              <Upload className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <p className="text-sm text-foreground">
                Connection acting up? You can always add transactions by hand — CSV
                and OFX upload stays open on the{' '}
                <Link
                  to="/admin/finance/settings/sources"
                  className="underline font-medium text-foreground"
                >
                  Sources
                </Link>{' '}
                tab.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {openers}
    </>
  );
}

export default BankConnectionsCard;
